import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Text, TextField, Badge, Flex, Spinner } from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";

type MultiAutocompleteBaseProps<O> = {
  value: number[];
  onChange: (value: number[]) => void;
  label: string;
  labelWeight?: "medium" | "bold";
  placeholder?: string;
  readOnly?: boolean;
  searchFn: (searchText: string, limit?: number) => Promise<O[]>;
  fetchByIdsFn: (ids: number[]) => Promise<O[]>;
  getOptionLabel: (option: O) => string;
  getOptionValue: (option: O) => number;
};

export function MultiAutocompleteBase<O>({
  value,
  onChange,
  label,
  labelWeight = "bold",
  placeholder = "Search...",
  readOnly = false,
  searchFn,
  fetchByIdsFn,
  getOptionLabel,
  getOptionValue,
}: MultiAutocompleteBaseProps<O>) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<O[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<O[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedIdsRef = useRef<string>("");

  // Load selected options when value changes
  useEffect(() => {
    let cancelled = false;

    async function loadSelectedOptions() {
      const valueKey = value.slice().sort().join(",");

      if (value.length === 0) {
        setSelectedOptions([]);
        loadedIdsRef.current = "";
        return;
      }

      // Skip if we already loaded these exact IDs
      if (valueKey === loadedIdsRef.current) {
        return;
      }

      try {
        const options = await fetchByIdsFn(value);
        if (!cancelled) {
          setSelectedOptions(options);
          loadedIdsRef.current = valueKey;
        }
      } catch (err) {
        console.error("Failed to fetch options by IDs:", err);
      }
    }

    loadSelectedOptions();

    return () => {
      cancelled = true;
    };
  }, [value, fetchByIdsFn]);

  const searchWithDebounce = useCallback(
    (searchText: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!searchText.trim()) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchFn(searchText);
          // Filter out already selected options
          const filtered = results.filter(
            (opt) => !value.includes(getOptionValue(opt)),
          );
          setOptions(filtered);
        } catch (err) {
          console.error("Failed to search:", err);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [searchFn, value, getOptionValue],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    setOpen(true);
    searchWithDebounce(newValue);
  };

  const handleSelect = (option: O) => {
    const optionValue = getOptionValue(option);
    if (!value.includes(optionValue)) {
      const newValue = [...value, optionValue];
      setSelectedOptions([...selectedOptions, option]);
      onChange(newValue);
    }
    setInputValue("");
    setOptions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (optionValue: number) => {
    const newValue = value.filter((v) => v !== optionValue);
    setSelectedOptions(
      selectedOptions.filter((o) => getOptionValue(o) !== optionValue),
    );
    onChange(newValue);
  };

  const handleFocus = () => {
    if (inputValue.trim()) {
      setOpen(true);
      searchWithDebounce(inputValue);
    }
  };

  const handleBlur = () => {
    // Delay closing to allow click on dropdown items
    setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
    // Allow backspace to remove last tag when input is empty
    if (
      e.key === "Backspace" &&
      inputValue === "" &&
      selectedOptions.length > 0
    ) {
      const lastOption = selectedOptions[selectedOptions.length - 1];
      handleRemove(getOptionValue(lastOption));
    }
  };

  return (
    <Box ref={containerRef} style={{ position: "relative" }}>
      <Text as="div" size="2" mb="1" weight={labelWeight}>
        {label}
      </Text>

      {!readOnly && (
        <Box style={{ position: "relative" }}>
          <TextField.Root
            ref={inputRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={selectedOptions.length === 0 ? placeholder : ""}
          >
            <TextField.Slot side="right">
              {loading && <Spinner size="1" />}
            </TextField.Slot>
          </TextField.Root>
        </Box>
      )}

      {/* Tag cloud for selected items */}
      {selectedOptions.length > 0 && (
        <Flex
          gap="1"
          wrap="wrap"
          mt="2"
          style={{ maxHeight: "150px", overflowY: "auto" }}
        >
          {selectedOptions.map((option) => (
            <Badge
              key={getOptionValue(option)}
              size="2"
              variant="soft"
              style={{
                cursor: readOnly ? "default" : "pointer",
                paddingRight: readOnly ? undefined : "4px",
              }}
            >
              <Flex align="center" gap="1">
                <span>{getOptionLabel(option)}</span>
                {!readOnly && (
                  <Cross2Icon
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRemove(getOptionValue(option))}
                  />
                )}
              </Flex>
            </Badge>
          ))}
        </Flex>
      )}

      {/* Dropdown results */}
      {open && (options.length > 0 || (inputValue.trim() && !loading)) && (
        <Box
          style={{
            position: "absolute",
            top: selectedOptions.length > 0 ? "auto" : "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            backgroundColor: "var(--color-panel-solid)",
            border: "1px solid var(--gray-6)",
            borderRadius: "var(--radius-2)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "var(--shadow-3)",
          }}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <Box
                key={getOptionValue(option)}
                onClick={() => handleSelect(option)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--gray-4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <Text size="2">{getOptionLabel(option)}</Text>
              </Box>
            ))
          ) : (
            <Box style={{ padding: "8px 12px" }}>
              <Text size="2" color="gray">
                No results found
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
